import { createAction, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReduxRootState } from "../../shared/redux/store";
import { ReduxSliceState } from "../../shared/types";
import { SkuCode, SkuSyncResult, CheckAllTrackingResult, SchedulerRunHistoryItem, SchedulerRunStatus, CardFundingBackfillResult, CardFundingBackfillHistoryItem, CardFundingBackfillStatus, ChargeReconciliationRunHistoryItem, OrphanedCharge } from "../../api/types";
import { ReduxPlatformMaintenanceState } from "../types";

const initialState: ReduxPlatformMaintenanceState = {
    skusState: 'READY',
    skus: [],
    lastSyncResult: undefined,
    schedulerHistoryState: 'READY',
    schedulerHistory: [],
    schedulerRunState: 'READY',
    lastSchedulerRunResult: undefined,
    schedulerStatus: { isRunning: false },
    cardFundingHistoryState: 'READY',
    cardFundingHistory: [],
    cardFundingRunState: 'READY',
    lastCardFundingRunResult: undefined,
    cardFundingStatus: { isRunning: false },
    orphanedChargesHistoryState: 'READY',
    orphanedChargesHistory: [],
    orphanedChargesRunState: 'READY',
    orphanedChargesStatus: { isRunning: false },
    orphanedChargesOutstandingState: 'READY',
    orphanedChargesOutstanding: [],
    orphanedChargeReversalState: 'READY',
    reversingTransactionToken: undefined,
    orphanedChargesResolvedState: 'READY',
    orphanedChargesResolved: [],
};

export const loadSkus = createAction('platformMaintenance/loadSkus');
export const syncSkus = createAction('platformMaintenance/syncSkus');
export const loadSchedulerHistory = createAction('platformMaintenance/loadSchedulerHistory');
export const runSchedulerNow = createAction('platformMaintenance/runSchedulerNow');
// Sep 2026 - polled by the drawer while open (see SchedulerDrawer's own effect), independent of
// whether THIS session triggered a run - lets any admin see a run in progress regardless of who
// or what (the OS scheduler) started it.
export const loadSchedulerStatus = createAction('platformMaintenance/loadSchedulerStatus');

export const loadCardFundingHistory = createAction('platformMaintenance/loadCardFundingHistory');
export const runCardFundingBackfill = createAction('platformMaintenance/runCardFundingBackfill');
export const loadCardFundingStatus = createAction('platformMaintenance/loadCardFundingStatus');

export const loadOrphanedChargesHistory = createAction('platformMaintenance/loadOrphanedChargesHistory');
export const runOrphanedChargesCheck = createAction('platformMaintenance/runOrphanedChargesCheck');
export const loadOrphanedChargesStatus = createAction('platformMaintenance/loadOrphanedChargesStatus');
// Sep 2026 - the live "Needs Review" worklist - a Portal Admin opens the drawer and sees the
// current unresolved orphaned charges, computed fresh, not a stale run result.
export const loadOrphanedChargesOutstanding = createAction('platformMaintenance/loadOrphanedChargesOutstanding');
export const reverseOrphanedCharge = createAction<{ transactionToken: string; reason: string }>('platformMaintenance/reverseOrphanedCharge');
// Sep 2026 - the "History" tab - charges already actioned (reversed), so a Portal Admin can see
// what happened to a row after it leaves the "Needs Review" list instead of it just disappearing.
export const loadOrphanedChargesResolved = createAction('platformMaintenance/loadOrphanedChargesResolved');

const platformMaintenanceSlice = createSlice({
    name: 'platformMaintenance',
    initialState,
    reducers: {
        setSkusState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.skusState = action.payload;
        },
        setSkus: (state, action: PayloadAction<SkuCode[]>) => {
            state.skus = action.payload;
        },
        setLastSyncResult: (state, action: PayloadAction<SkuSyncResult | undefined>) => {
            state.lastSyncResult = action.payload;
        },
        setSchedulerHistoryState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.schedulerHistoryState = action.payload;
        },
        setSchedulerHistory: (state, action: PayloadAction<SchedulerRunHistoryItem[]>) => {
            state.schedulerHistory = action.payload;
        },
        setSchedulerRunState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.schedulerRunState = action.payload;
        },
        setLastSchedulerRunResult: (state, action: PayloadAction<CheckAllTrackingResult | undefined>) => {
            state.lastSchedulerRunResult = action.payload;
        },
        setSchedulerStatus: (state, action: PayloadAction<SchedulerRunStatus>) => {
            state.schedulerStatus = action.payload;
        },
        setCardFundingHistoryState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.cardFundingHistoryState = action.payload;
        },
        setCardFundingHistory: (state, action: PayloadAction<CardFundingBackfillHistoryItem[]>) => {
            state.cardFundingHistory = action.payload;
        },
        setCardFundingRunState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.cardFundingRunState = action.payload;
        },
        setLastCardFundingRunResult: (state, action: PayloadAction<CardFundingBackfillResult | undefined>) => {
            state.lastCardFundingRunResult = action.payload;
        },
        setCardFundingStatus: (state, action: PayloadAction<CardFundingBackfillStatus>) => {
            state.cardFundingStatus = action.payload;
        },
        setOrphanedChargesHistoryState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.orphanedChargesHistoryState = action.payload;
        },
        setOrphanedChargesHistory: (state, action: PayloadAction<ChargeReconciliationRunHistoryItem[]>) => {
            state.orphanedChargesHistory = action.payload;
        },
        setOrphanedChargesRunState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.orphanedChargesRunState = action.payload;
        },
        setOrphanedChargesStatus: (state, action: PayloadAction<SchedulerRunStatus>) => {
            state.orphanedChargesStatus = action.payload;
        },
        setOrphanedChargesOutstandingState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.orphanedChargesOutstandingState = action.payload;
        },
        setOrphanedChargesOutstanding: (state, action: PayloadAction<OrphanedCharge[]>) => {
            state.orphanedChargesOutstanding = action.payload;
        },
        setOrphanedChargeReversalState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.orphanedChargeReversalState = action.payload;
        },
        setReversingTransactionToken: (state, action: PayloadAction<string | undefined>) => {
            state.reversingTransactionToken = action.payload;
        },
        setOrphanedChargesResolvedState: (state, action: PayloadAction<ReduxSliceState>) => {
            state.orphanedChargesResolvedState = action.payload;
        },
        setOrphanedChargesResolved: (state, action: PayloadAction<OrphanedCharge[]>) => {
            state.orphanedChargesResolved = action.payload;
        },
    },
});

export default platformMaintenanceSlice;

const selectSelf = (state: ReduxRootState) => state.platformMaintenance;
export const selectPlatformMaintenanceSkusIsBusy = createSelector(selectSelf, self => self.skusState === 'BUSY');
export const selectPlatformMaintenanceSkus = createSelector(selectSelf, self => self.skus);
export const selectPlatformMaintenanceLastSyncResult = createSelector(selectSelf, self => self.lastSyncResult);
export const selectPlatformMaintenanceSchedulerHistoryIsBusy = createSelector(selectSelf, self => self.schedulerHistoryState === 'BUSY');
export const selectPlatformMaintenanceSchedulerHistory = createSelector(selectSelf, self => self.schedulerHistory);
export const selectPlatformMaintenanceSchedulerRunIsBusy = createSelector(selectSelf, self => self.schedulerRunState === 'BUSY');
export const selectPlatformMaintenanceLastSchedulerRunResult = createSelector(selectSelf, self => self.lastSchedulerRunResult);
export const selectPlatformMaintenanceSchedulerIsRunning = createSelector(selectSelf, self => self.schedulerStatus.isRunning);
export const selectPlatformMaintenanceSchedulerRunningSinceUtc = createSelector(selectSelf, self => self.schedulerStatus.runningSinceUtc);
export const selectPlatformMaintenanceCardFundingHistoryIsBusy = createSelector(selectSelf, self => self.cardFundingHistoryState === 'BUSY');
export const selectPlatformMaintenanceCardFundingHistory = createSelector(selectSelf, self => self.cardFundingHistory);
export const selectPlatformMaintenanceCardFundingRunIsBusy = createSelector(selectSelf, self => self.cardFundingRunState === 'BUSY');
export const selectPlatformMaintenanceLastCardFundingRunResult = createSelector(selectSelf, self => self.lastCardFundingRunResult);
export const selectPlatformMaintenanceCardFundingIsRunning = createSelector(selectSelf, self => self.cardFundingStatus.isRunning);
export const selectPlatformMaintenanceCardFundingRunningSinceUtc = createSelector(selectSelf, self => self.cardFundingStatus.runningSinceUtc);
export const selectPlatformMaintenanceOrphanedChargesHistoryIsBusy = createSelector(selectSelf, self => self.orphanedChargesHistoryState === 'BUSY');
export const selectPlatformMaintenanceOrphanedChargesHistory = createSelector(selectSelf, self => self.orphanedChargesHistory);
export const selectPlatformMaintenanceOrphanedChargesRunIsBusy = createSelector(selectSelf, self => self.orphanedChargesRunState === 'BUSY');
export const selectPlatformMaintenanceOrphanedChargesIsRunning = createSelector(selectSelf, self => self.orphanedChargesStatus.isRunning);
export const selectPlatformMaintenanceOrphanedChargesRunningSinceUtc = createSelector(selectSelf, self => self.orphanedChargesStatus.runningSinceUtc);
export const selectPlatformMaintenanceOrphanedChargesOutstandingIsBusy = createSelector(selectSelf, self => self.orphanedChargesOutstandingState === 'BUSY');
export const selectPlatformMaintenanceOrphanedChargesOutstanding = createSelector(selectSelf, self => self.orphanedChargesOutstanding);
export const selectPlatformMaintenanceOrphanedChargeReversalIsBusy = createSelector(selectSelf, self => self.orphanedChargeReversalState === 'BUSY');
export const selectPlatformMaintenanceReversingTransactionToken = createSelector(selectSelf, self => self.reversingTransactionToken);
export const selectPlatformMaintenanceOrphanedChargesResolvedIsBusy = createSelector(selectSelf, self => self.orphanedChargesResolvedState === 'BUSY');
export const selectPlatformMaintenanceOrphanedChargesResolved = createSelector(selectSelf, self => self.orphanedChargesResolved);
